import { ItemValue, MatrixRowModel, Question, QuestionCheckboxModel, QuestionCompositeModel, QuestionCustomModel, QuestionDropdownModel, QuestionFileModel, QuestionMatrixDropdownModel, QuestionMatrixDynamicModel, QuestionMatrixModel, QuestionPanelDynamicModel, QuestionRadiogroupModel, QuestionSelectBase, QuestionTagboxModel, settings } from "survey-core";
import { createImagesContainer, createLinksContainer } from "../utils";
import { ICellData, IColumn, ColumnDataType, QuestionLocation, IColumnData } from "./config";
import { ITableOptions, Table } from "./table";

export class BaseColumn<T extends Question = Question> implements IColumn {
  dataType: ColumnDataType;
  isVisible: boolean = true;
  isPublic: boolean = true;
  location: QuestionLocation = QuestionLocation.Column;
  width?: string | number;
  visibleIndex?: number;
  isComment?: boolean;
  private nameValue: string;
  private displayNameValue?: string;

  constructor(protected question: T, protected table: Table) {
    this.dataType = this.getDataType();
    this.updateWhenQuestionIsReady(question, table);
  }

  protected updateWhenQuestionIsReady(question: Question, table: Table) {
    if(!question || question.isReady) return;
    question.waitForQuestionIsReady().then(() => {
      if(!table.isInitialized) return;
      try {
        table.lockStateChanged();
        table.refresh(!table.isInitTableDataProcessing);
      } finally {
        table.unlockStateChanged();
      }
    });
  }

  get name(): string {
    if(!this.nameValue) {
      this.name = this.getName();
    }
    return this.nameValue;
  }
  set name(val: string) {
    this.nameValue = val;
  }
  get displayName(): string {
    if(!this.displayNameValue) {
      this.displayName = this.getDisplayName();
    }
    return this.displayNameValue;
  }
  public set displayName(val: string) {
    this.displayNameValue = val;
  }

  protected getDisplayName(): string {
    return this.table.useNamesAsTitles
      ? this.question.name
      : (this.question.locTitle?.renderedHtml || this.question.title || "").trim() || this.question.name;
  }
  protected getName(): string {
    return this.question.name;
  }
  protected getDataType(): ColumnDataType {
    return ColumnDataType.Text;
  }
  protected getDisplayValueCore(data: any) {
    return data[this.name];
  }

  protected getDisplayValue(data: any, table: Table, options: ITableOptions): any {
    let displayValue = this.getDisplayValueCore(data);
    const question = this.question;

    if(!!question) {
      if(options.useValuesAsLabels) {
        displayValue = question.value;
      } else {
        displayValue = question.displayValue;
      }
    }
    return displayValue;
  }
  private formatDisplayValue(displayValue: any) {
    return typeof displayValue === "string"
      ? displayValue
      : JSON.stringify(displayValue) || "";
  }

  public getCellData(table: Table, data: any): ICellData {
    const displayValue = this.getDisplayValue(data, table, table.options);
    return { question: this.question, displayValue: this.formatDisplayValue(displayValue) };
  }
  public toJSON(): IColumnData {
    return {
      name: this.name,
      displayName: this.displayName,
      dataType: this.dataType,
      isVisible: this.isVisible,
      isPublic: this.isPublic,
      location: this.location
    };
  }
  public fromJSON(data: IColumnData) {
    Object.keys(data).forEach(key => {
      this[key] = data[key];
    });
  }
}

export class DefaultColumn extends BaseColumn {
  protected getDisplayValue(data: any, table: Table, options: ITableOptions): any {
    return this.getDisplayValueCore(data);
  }
}

export class SelectBaseColumn<T extends QuestionSelectBase> extends BaseColumn<T> {
  protected get isOtherInSeparateColumn() {
    return this.question.hasOther && this.question.getStoreOthersAsComment();
  }
  protected getItemDisplayText(item: ItemValue, options: ITableOptions): string {
    return options.useValuesAsLabels ? item.value : item.textOrHtml;
  }
  protected getDisplayValue(data: any, table: Table, options: ITableOptions): string {
    const prevSeparator = settings.choicesSeparator;
    settings.choicesSeparator = table.itemsDelimiter;
    const value = options.useValuesAsLabels ? this.question.renderedValue : this.question.displayValue;
    settings.choicesSeparator = prevSeparator;
    return value;
  }
}

export class CheckboxColumn extends SelectBaseColumn<QuestionCheckboxModel | QuestionTagboxModel> {
  protected getDisplayValue(data: any, table: Table, options: ITableOptions): string {
    if(this.isOtherInSeparateColumn) {
      const selectedItems = this.question.selectedItems;
      const res: Array<string> = selectedItems.map(item => this.getItemDisplayText(item, options));
      return res.join(table.itemsDelimiter);
    }
    return super.getDisplayValue(data, table, options);
  }
}

export class FlattenedCheckboxColumn extends BaseColumn<QuestionCheckboxModel | QuestionTagboxModel> {
  constructor(question: QuestionCheckboxModel | QuestionTagboxModel, private choiceValue: any, private choiceText: string, table: Table) {
    super(question, table);
  }

  protected getDataType(): ColumnDataType {
    return ColumnDataType.Html;
  }

  protected getName(): string {
    return `${this.question.name}.${this.choiceValue}`;
  }

  protected getDisplayName(): string {
    const questionDisplayName = this.table.useNamesAsTitles
      ? this.question.name
      : (this.question.locTitle?.renderedHtml || this.question.title || "").trim() || this.question.name;
    const choiceDisplayText = this.table.useNamesAsTitles ? this.choiceValue : this.choiceText;
    return `${questionDisplayName} - ${choiceDisplayText}`;
  }

  protected getDisplayValue(data: any, table: Table, options: ITableOptions): any {
    const questionValue = data[this.question.name];
    if(!Array.isArray(questionValue)) {
      return "";
    }
    const index = questionValue.indexOf(this.choiceValue);
    if(index < 0) {
      return "";
    }
    // Default to checkmark if not specified
    const displayMode = options.multiSelectColumnValueFormat || "checkmark";
    return displayMode === "checkmark" ? "&#10004;" : (index + 1).toString();
  }
}

export class SingleChoiceColumn extends SelectBaseColumn<QuestionDropdownModel | QuestionRadiogroupModel> {
  protected getDisplayValue(data: any, table: Table, options: ITableOptions): string {
    if(this.isOtherInSeparateColumn) {
      const selectedItem = this.question.selectedItem;
      if(!selectedItem) return "";
      return this.getItemDisplayText(selectedItem, options);
    }
    return super.getDisplayValue(data, table, options);
  }
}

export class CommentColumn<T extends Question = Question> extends BaseColumn<T> {
  protected getName(): string {
    return `${this.question.name}${settings.commentPrefix}`;
  }
  protected getDisplayName(): string {
    return this.question.commentText;
  }
  protected getDisplayValue(data: any, table: Table, options: ITableOptions): string {
    return this.question.comment;
  }
}
export class OtherColumn extends CommentColumn<QuestionSelectBase> {
  protected getDisplayName(): string {
    return this.question.otherText;
  }
}

export class MatrixColumn extends BaseColumn<QuestionMatrixModel> {
  private valueName: string;
  private valuePath: string;
  constructor(question: QuestionMatrixModel, private row: MatrixRowModel, table: Table) {
    super(question, table);
    this.valueName = this.question.name;
    this.valuePath = this.row?.value;
  }
  protected getName(): string {
    return this.question.name + "." + this.row?.value;
  }
  protected getDisplayName(): string {
    const table = this.table;
    const question = this.question;
    const row = this.row;
    return (table.useNamesAsTitles
      ? question.name
      : (question.title || "").trim() || question.name) + " - " + (table.useNamesAsTitles ? row?.value : row?.locText.textOrHtml);
  }

  protected getDisplayValue(data: any, table: Table, options: ITableOptions) {
    let displayValue = data[this.valueName];
    if(this.valuePath && typeof displayValue === "object") {
      displayValue = displayValue[this.valuePath];
      if(displayValue !== undefined) {
        if(Array.isArray(displayValue)) {
          const res = [];
          displayValue.forEach(itemValue => {
            const item = ItemValue.getItemByValue(this.question.columns, itemValue);
            if(!!item) {
              res.push(options.useValuesAsLabels ? item.value : item.locText.textOrHtml);
            }
          }
          );
          displayValue = res.join(table.itemsDelimiter);
        } else {
          const choiceValue = ItemValue.getItemByValue(this.question.columns, displayValue);
          if(!!choiceValue) {
            displayValue = options.useValuesAsLabels ? choiceValue.value : choiceValue.locText.textOrHtml;
          }
        }
      }
    }
    return displayValue;
  }
}

export class ImageColumn extends BaseColumn {
  protected getDataType(): ColumnDataType {
    return ColumnDataType.Image;
  }
}

export class FileColumn extends BaseColumn<QuestionFileModel> {
  protected getDataType(): ColumnDataType {
    return ColumnDataType.FileLink;
  }
  protected getDisplayValue(data: any, table: Table, options: ITableOptions) {
    let displayValue = this.getDisplayValueCore(data);
    if(Array.isArray(displayValue)) {
      displayValue = Table.showFilesAsImages ? createImagesContainer(
        displayValue
      ).outerHTML : createLinksContainer(
        displayValue
      ).outerHTML;
    }
    return displayValue;
  }
}
export class MatrixDropdownColumn extends BaseColumn<QuestionMatrixDropdownModel> {
  private rowValue: string;
  private colName: string;
  constructor(question: QuestionMatrixDropdownModel, protected row, protected col, table: Table) {
    super(question, table);
    this.rowValue = this.row.value;
    this.colName = this.col.name;
  }
  protected getName(): string {
    return this.question.name + "." + this.row.value + "." + this.col.name;
  }
  protected getDisplayName(): string {
    const table = this.table;
    const question = this.question;
    return (this.table.useNamesAsTitles
      ? question.name
      : (question.title || "").trim() || question.name) + " - " + (table.useNamesAsTitles ? this.row.value : this.row.locText.textOrHtml) + " - " + (table.useNamesAsTitles ? this.col.name : this.col.locTitle.textOrHtml);

  }
  protected getDisplayValue(data: any, table: Table, options: ITableOptions) {
    let displayValue = data[this.question.name];
    const question = this.question;
    if(this.rowValue && this.colName && typeof displayValue === "object") {
      let [rowId, colId] = [this.rowValue, this.colName];
      displayValue = question.value;
      if(!options.useValuesAsLabels) {
        displayValue = question.displayValue;
        rowId = question.rows.filter(row => row.value === this.rowValue)[0].text;
        colId = question.getColumnByName(this.colName).title;
      }
      displayValue = (displayValue[rowId] && displayValue[rowId][colId]) || "";
    }
    return displayValue;
  }
}

export class CustomQuestionColumn extends BaseColumn<QuestionCustomModel> {
  constructor(question: QuestionCustomModel, table: Table) {
    super(question, table);
    this.updateWhenQuestionIsReady(question.contentQuestion, table);
  }
}

export class CompositeQuestionColumn extends BaseColumn<QuestionCompositeModel> {
  constructor(question: QuestionCompositeModel, table: Table) {
    super(question, table);
    const questionList: Question[] = [];
    this.question.contentPanel.addQuestionsToList(questionList);
    questionList.forEach((q: Question) => {
      this.updateWhenQuestionIsReady(q, table);
    });
  }
}

export class MatrixDynamicColumn extends BaseColumn<QuestionMatrixDynamicModel> {
  protected getDataType(): ColumnDataType {
    return this.table.options.useNestedTables ? ColumnDataType.NestedTable : ColumnDataType.Text;
  }

  protected getDisplayValue(data: any, table: Table, options: ITableOptions): any {
    if(table.options.useNestedTables) {
      return this.getDisplayValueCore(data);
    }
    return super.getDisplayValue(data, table, options);
  }

  public getCellData(table: Table, data: any): ICellData {
    const displayValue = this.getDisplayValue(data, table, table.options);
    const formattedValue = table.options.useNestedTables && Array.isArray(displayValue)
      ? displayValue
      : (typeof displayValue === "string" ? displayValue : JSON.stringify(displayValue) || "");
    return { question: this.question, displayValue: formattedValue };
  }
}

export class PanelDynamicColumn extends BaseColumn<QuestionPanelDynamicModel> {
  protected getDataType(): ColumnDataType {
    return this.table.options.useNestedTables ? ColumnDataType.NestedTable : ColumnDataType.Text;
  }

  protected getDisplayValue(data: any, table: Table, options: ITableOptions): any {
    if(table.options.useNestedTables) {
      return this.getDisplayValueCore(data);
    }
    return super.getDisplayValue(data, table, options);
  }

  public getCellData(table: Table, data: any): ICellData {
    const displayValue = this.getDisplayValue(data, table, table.options);
    const formattedValue = table.options.useNestedTables && Array.isArray(displayValue)
      ? displayValue
      : (typeof displayValue === "string" ? displayValue : JSON.stringify(displayValue) || "");
    return { question: this.question, displayValue: formattedValue };
  }
}